import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { CheckIcon, ChevronDown, XIcon } from "lucide-react";

import { cn } from "@/lib/utils";
import { Badge } from "@/components/ui/badge";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Button } from "@/components/ui/button";
import { resolvePluralsChose } from "@/service/text/plurals";
import { useEffect, useMemo, useState } from "react";

/**
 * Variants for the multi-select component to handle different styles.
 * Uses class-variance-authority (cva) to define different styles based on "variant" prop.
 */
const multiSelectVariants = cva(
  "m-1 transition ease-in-out delay-150 hover:-translate-y-1 hover:scale-110 duration-300",
  {
    variants: {
      variant: {
        default:
          "border-foreground/10 text-foreground bg-card hover:bg-card/80",
        secondary:
          "border-foreground/10 bg-secondary text-secondary-foreground hover:bg-secondary/80",
        destructive:
          "border-transparent bg-destructive text-destructive-foreground hover:bg-destructive/80",
        inverted: "inverted",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  },
);

/**
 * Props for MultiSelect component
 */
interface MultiSelectProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof multiSelectVariants> {
  /**
   * An array of option objects to be displayed in the multi-select component.
   * Each option object has a label, value, and an optional icon.
   */
  options: {
    /** The text to display for the option. */
    name: string;
    /** The unique value associated with the option. */
    value: string;
    /** Optional icon component to display alongside the option. */
    icon?: React.ComponentType<{ className?: string }>;
  }[];

  /**
   * Callback function triggered when the selected values change.
   * Receives an array of the new selected values.
   */
  onValueChange: (value: string[]) => void;

  /** The default selected values when the component mounts. */
  defaultValue?: string[];

  placeholder?: string;
  searchPlaceholder?: string;

  /**
   * Maximum number of items to display. Extra selected items will be summarized.
   * Optional, defaults to 3.
   */
  maxCount?: number;

  /**
   * The modality of the popover. When set to true, interaction with outside elements
   * will be disabled and only popover content will be visible to screen readers.
   * Optional, defaults to false.
   */
  modalPopover?: boolean;

  /**
   * If true, renders the multi-select component as a child of another component.
   * Optional, defaults to false.
   */
  asChild?: boolean;

  /**
   * Additional class names to apply custom styles to the multi-select component.
   * Optional, can be used to add custom styles.
   */
  className?: string;

  /**
   * Renders the search field and the list inline, without the popover trigger — for formularze,
   * w których wybór z listy jest głównym krokiem i chowanie go za przyciskiem tylko dokłada klik.
   * Optional, defaults to false.
   */
  inline?: boolean;
}

export const MultiSelect = React.forwardRef<
  HTMLButtonElement,
  MultiSelectProps
>(function MultiSelect(
  {
    options,
    onValueChange,
    defaultValue = [],
    placeholder = "Wybierz...",
    searchPlaceholder,
    modalPopover = false,
    className,
    inline = false,
    ...props
  },
  ref,
) {
  const [selectedValues, setSelectedValues] = useState<string[]>(defaultValue);

  // Wartość zmieniona z zewnątrz (np. filtr odtworzony z localStorage, wstecz w przeglądarce) — przejmij ją.
  // Po własnym kliknięciu defaultValue wraca równe selectedValues, więc to no-op.
  const defaultValueKey = JSON.stringify(defaultValue);
  useEffect(() => {
    setSelectedValues(JSON.parse(defaultValueKey));
  }, [defaultValueKey]);
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const [search, setSearch] = useState("");

  const handleInputKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter") {
      setIsPopoverOpen(true);
    } else if (event.key === "Backspace" && !event.currentTarget.value) {
      const newSelectedValues = [...selectedValues];
      newSelectedValues.pop();
      setSelectedValues(newSelectedValues);
      onValueChange(newSelectedValues);
    }
  };

  const toggleOption = (option: string) => {
    const newSelectedValues = selectedValues.includes(option)
      ? selectedValues.filter((value) => value !== option)
      : [...selectedValues, option];
    setSelectedValues(newSelectedValues);
    onValueChange(newSelectedValues);
  };

  const handleClear = () => {
    setSelectedValues([]);
    onValueChange([]);
  };

  const handleTogglePopover = () => {
    setIsPopoverOpen((prev) => !prev);
  };

  const selectedAtLeastTwoItems = selectedValues.length;

  // Filtrowanie po stronie komponentu jest potrzebne TYLKO w trybie inline: pasek nad listą
  // zaznacza „wszystkie z wyników", więc musimy wiedzieć, co user właśnie widzi — a cmdk trzyma
  // wynik swojego filtra u siebie. W popoverze zostaje filtr cmdk, żeby nic tam nie zmieniać.
  const normalize = (text: string) =>
    text
      .toLowerCase()
      .normalize("NFD")
      .replace(/\p{Diacritic}/gu, "");

  const visibleOptions = useMemo(() => {
    if (!inline || !search.trim()) {
      return options;
    }

    const needle = normalize(search.trim());

    return options.filter((option) => normalize(option.name).includes(needle));
  }, [inline, options, search]);

  const unselectedVisible = visibleOptions.filter(
    ({ value }) => !selectedValues.includes(value),
  );

  const selectVisible = () => {
    const newSelectedValues = [
      ...selectedValues,
      ...unselectedVisible.map(({ value }) => value),
    ];
    setSelectedValues(newSelectedValues);
    onValueChange(newSelectedValues);
  };

  const commandBlock = (
    <Command shouldFilter={!inline}>
      <CommandInput
        placeholder={searchPlaceholder ?? placeholder}
        onKeyDown={handleInputKeyDown}
        {...(inline ? { value: search, onValueChange: setSearch } : {})}
      />
      {/* Pasek stanu: bez niego przy kilkudziesięciu pozycjach zaznaczenia wyjeżdżają poza widok
          i user nie wie, co ma wybrane, dopóki nie przewinie listy z powrotem. */}
      {inline && (
        <div className="flex items-center justify-between gap-2 border-b px-3 py-2">
          <span className="text-muted-foreground text-xs">
            Zaznaczone: {selectedValues.length}
          </span>
          <div className="flex items-center gap-3">
            {unselectedVisible.length > 0 && (
              <Button
                type="button"
                variant="inline"
                size="inline"
                className="text-xs"
                onClick={selectVisible}
              >
                {search.trim()
                  ? "Zaznacz wszystkie z wyników"
                  : "Zaznacz wszystkie"}
              </Button>
            )}
            {selectedValues.length > 0 && (
              <Button
                type="button"
                variant="inline"
                size="inline"
                className="text-muted-foreground text-xs"
                onClick={handleClear}
              >
                Wyczyść
              </Button>
            )}
          </div>
        </div>
      )}
      {/* sm:max-h-auto nie istnieje w Tailwindzie, więc lista wszędzie miała 150 px
          i mieściła pięć pozycji — na desktopie pokazujemy realnie więcej. Inline stoi na stronie
          na stałe (nie jest chwilową nakładką), więc może zabrać więcej wysokości. */}
      <CommandList
        className={cn(
          "max-h-[150px]",
          inline ? "sm:max-h-[420px]" : "sm:max-h-[320px]",
        )}
      >
        <CommandEmpty>Brak wyników.</CommandEmpty>
        <CommandGroup>
          {visibleOptions.map((option) => {
            const isSelected = selectedValues.includes(option.value);
            return (
              <CommandItem
                key={option.value}
                onSelect={() => toggleOption(option.value)}
                className="cursor-pointer"
              >
                <div
                  className={cn(
                    "border-remedium-blue mr-2 flex h-4 w-4 items-center justify-center rounded-sm border",
                    isSelected
                      ? "bg-remedium-blue text-primary-foreground"
                      : "opacity-50 [&_svg]:invisible",
                  )}
                >
                  <CheckIcon className="h-4 w-4 stroke-white" />
                </div>
                <span>{option.name}</span>
              </CommandItem>
            );
          })}
        </CommandGroup>
      </CommandList>
    </Command>
  );

  if (inline) {
    return (
      <div
        ref={ref as unknown as React.Ref<HTMLDivElement>}
        className={cn("w-full overflow-hidden rounded-md border", className)}
      >
        {commandBlock}
      </div>
    );
  }

  return (
    <Popover
      open={isPopoverOpen}
      onOpenChange={setIsPopoverOpen}
      modal={modalPopover}
    >
      <PopoverTrigger asChild>
        <div
          ref={ref}
          {...props}
          onClick={handleTogglePopover}
          className={cn(
            "inline-flex items-center justify-center gap-2 rounded-lg text-sm font-medium whitespace-nowrap transition-all hover:cursor-pointer focus-visible:ring-1 focus-visible:outline-none disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
            "flex h-auto min-h-10 w-full scale-100! items-center justify-between rounded-md border bg-white px-4 py-2 [&_svg]:pointer-events-auto",
            className,
          )}
        >
          {selectedAtLeastTwoItems > 0 ? (
            <div className="flex w-full items-center justify-between">
              <div className="flex w-[calc(100%-32px)] flex-wrap items-center">
                {selectedAtLeastTwoItems > 1 ? (
                  <div className={"text-black"}>
                    {resolvePluralsChose(selectedAtLeastTwoItems)}
                  </div>
                ) : (
                  <Badge
                    onClick={() => {
                      handleClear();
                    }}
                    className={
                      "max-w-full justify-start truncate overflow-hidden bg-indigo-100 font-semibold text-black"
                    }
                  >
                    {
                      options.find(({ value }) => value === selectedValues[0])
                        ?.name
                    }{" "}
                    <XIcon className={"[&_path]:stroke-black"} />
                  </Badge>
                )}
              </div>
              <div
                className="flex items-center justify-between"
                onClick={selectedAtLeastTwoItems ? handleClear : null}
              >
                {selectedAtLeastTwoItems > 1 ? (
                  <XIcon className={"[&_path]:stroke-black"} />
                ) : (
                  <ChevronDown className="text-foreground h-4 cursor-pointer" />
                )}
              </div>
            </div>
          ) : (
            <div className="mx-auto flex w-full items-center justify-between">
              <span className="text-foreground mx-0 max-w-[75%] truncate text-sm font-normal">
                {placeholder}
              </span>
              <ChevronDown className="text-foreground h-4 cursor-pointer" />
            </div>
          )}
        </div>
      </PopoverTrigger>
      <PopoverContent
        className="font-inter popover-content-width-same-as-its-trigger z-[99999999999999999] w-auto p-0"
        align="start"
        onEscapeKeyDown={() => setIsPopoverOpen(false)}
      >
        {commandBlock}
      </PopoverContent>
    </Popover>
  );
});
